import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useSelector } from 'react-redux';
import i18n from 'i18n-js';

import Selectors from '@styles/selectors';
import FilterButton from './FilterButton';

import { fetchNoMachineColumnCount, selectPlanboardMachineColumn, selectSelectedMachienGroupId } from '@reducers/v2/planboard';

import { font } from '@styles/fonts';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { Machine } from '@interfaces/machines';
import { useAppDispatch } from '@store/hooks/useAppDispatch';

interface NoMachineColumnFilterProps {
  scale: ScaleFunction;
  font: typeof font;
  setFilter: React.Dispatch<React.SetStateAction<string>>;
}

type FilterOption = (typeof FILTER_OPTIONS)[keyof typeof FILTER_OPTIONS];

const FILTER_OPTIONS = {
  ALL: 'all',
  LINKED: 'linked',
} as const;

const filterOptionsList = [
  { title: i18n.t('All Operations'), value: FILTER_OPTIONS.ALL },
  {
    title: i18n.t('Linked Operations'),
    value: FILTER_OPTIONS.LINKED,
  },
];

const NoMachineColumnFilter: React.FC<NoMachineColumnFilterProps> = ({ font, scale, setFilter }) => {
  const dispatch = useAppDispatch();

  const selectedMachineGroupId = useSelector(selectSelectedMachienGroupId);
  const machines = useSelector<any>((state) => state.machines.machines) as Machine[];
  const columnRecord = useSelector(selectPlanboardMachineColumn(-1));
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>(() =>
    typeof selectedMachineGroupId === 'number' ? FILTER_OPTIONS.LINKED : FILTER_OPTIONS.ALL
  );

  const linkedOperationsFilter = useMemo(() => {
    const operations =
      machines?.reduce<Set<number>>((acc, machine) => {
        const isMachineIncluded = !selectedMachineGroupId || machine.machineGroups.some((group) => group.machineGroupId === selectedMachineGroupId);

        if (!isMachineIncluded) {
          return acc;
        } else {
          machine.operations.forEach((operation) => acc.add(operation.operationId));
        }

        return acc;
      }, new Set()) ?? [];

    const linkedFilter =
      [...operations].map((operationId) => {
        return `OperationId eq ${operationId}`;
      }) ?? [];

    return linkedFilter.join(' or ');
  }, [machines, selectedMachineGroupId]);

  useEffect(() => {
    setFilter(selectedFilter === FILTER_OPTIONS.ALL ? '' : linkedOperationsFilter);
    dispatch(fetchNoMachineColumnCount({ filter: linkedOperationsFilter }));
  }, [selectedFilter, linkedOperationsFilter]);

  useEffect(() => {
    setSelectedFilter(typeof selectedMachineGroupId === 'number' ? FILTER_OPTIONS.LINKED : FILTER_OPTIONS.ALL);
  }, [selectedMachineGroupId]);

  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: scale(9),
        marginBottom: scale(12),
        marginTop: scale(3),
        paddingInline: scale(18),
      }}
    >
      {filterOptionsList.map(({ title, value }) => {
        const count = value === FILTER_OPTIONS.ALL ? columnRecord?.paginationMetadata?.totalCount : columnRecord?.paginationMetadata?.linkedCount;
        const isSelected = value === selectedFilter;

        return (
          <FilterButton
            key={value}
            font={font}
            scale={scale}
            selected={isSelected}
            title={title}
            count={count ?? 0}
            onClick={() => setSelectedFilter(value)}
            loading={columnRecord?.loading}
          />
        );
      })}
    </View>
  );
};

export default NoMachineColumnFilter;
